if (!customElements.get("order-sample")) {
  customElements.define(
    "order-sample",
    class OrderSample extends HTMLElement {
      constructor() {
        super();
        this.button = this.querySelector('button[type="submit"]');
        this.form = this.querySelector('form');
        
        // Safely get form values
        const idInput = this.form.querySelector('input[name="id"]');
        const quantityInput = this.form.querySelector('input[name="quantity"]');
        
        this.productId = idInput ? idInput.value : null;
        this.quantity = quantityInput ? quantityInput.value : '1';
        
        // Get product info for popup
        this.productTitle = this.getProductTitle();
        this.productPrice = this.getProductPrice();
      }

      getProductTitle() {
        // Try multiple selectors to find product title
        const titleSelectors = [
          '[data-section][data-product] .text-4xl\\/solid',
          '[data-section][data-product] h1',
          '.product-title',
          'h1'
        ];
        
        for (const selector of titleSelectors) {
          const titleElement = document.querySelector(selector);
          if (titleElement && titleElement.textContent.trim()) {
            return titleElement.textContent.trim();
          }
        }
        
        // Fallback to window.product or generic
        return (window.product && window.product.title) || 'Product';
      }

      connectedCallback() {
        this.form.addEventListener("submit", this.handleSubmit.bind(this));
      }

      getProductPrice() {
        // Try to get price from product data
        const productData = window.product || {};
        if (productData.price) {
          return this.formatMoney(productData.price);
        }
        // Fallback - you might want to adjust this based on your theme structure
        return "Contact for price";
      }

      formatMoney(cents) {
        if (window.theme && window.theme.moneyFormat) {
          // Use Shopify's money format if available
          return window.theme.moneyFormat.replace('{{amount}}', (cents / 100).toFixed(2));
        }
        // Fallback formatting
        const money = cents / 100;
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD'
        }).format(money);
      }

      async handleSubmit(event) {
        event.preventDefault();
        
        // Validate we have a product ID
        if (!this.productId) {
          this.showErrorMessage('Product ID is missing. Please refresh the page and try again.');
          return;
        }
        
        // Show loading state
        this.setButtonState('loading');
        
        try {
          const formData = new FormData();
          formData.append('id', this.productId);
          formData.append('quantity', this.quantity);

          const response = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData
          });

          const result = await response.json();
          
          if (response.ok) {
            // Success - show popup
            this.setButtonState('default');
            this.showSuccessPopup();
          } else {
            // Error handling
            this.setButtonState('error');
            console.error('Error adding to cart:', result);
            
            // Show error message
            let errorMessage = result.message || result.description || 'Error adding item to cart';
            this.showErrorMessage(errorMessage);
          }
        } catch (error) {
          this.setButtonState('error');
          console.error('Network error:', error);
          this.showErrorMessage('Network error. Please try again.');
        }
      }

      setButtonState(state) {
        const buttonText = this.button.querySelector('.default-context');
        const loadingText = this.button.querySelector('.loading-context') || this.createLoadingContext();
        
        switch (state) {
          case 'loading':
            this.button.setAttribute('disabled', '');
            this.button.dataset.state = 'loading';
            if (buttonText) buttonText.style.opacity = '0';
            if (loadingText) loadingText.style.opacity = '1';
            break;
          case 'error':
            this.button.removeAttribute('disabled');
            this.button.dataset.state = 'error';
            if (buttonText) buttonText.style.opacity = '1';
            if (loadingText) loadingText.style.opacity = '0';
            setTimeout(() => {
              this.button.removeAttribute('data-state');
            }, 3000);
            break;
          case 'default':
            this.button.removeAttribute('disabled');
            this.button.removeAttribute('data-state');
            if (buttonText) buttonText.style.opacity = '1';
            if (loadingText) loadingText.style.opacity = '0';
            break;
        }
      }

      createLoadingContext() {
        const loadingSpan = document.createElement('span');
        loadingSpan.className = 'absolute flex items-center opacity-0 transition-opacity loading-context';
        loadingSpan.innerHTML = `
          <svg class="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Adding...
        `;
        this.button.appendChild(loadingSpan);
        return loadingSpan;
      }

      showSuccessPopup() {
        // Create backdrop
        const backdrop = document.createElement('div');
        backdrop.className = 'fixed inset-0 bg-black bg-opacity-75 backdrop-blur-sm z-50 flex items-center justify-center p-4';
        backdrop.style.animation = 'fadeIn 0.3s ease-out';
        
        // Create popup
        const popup = document.createElement('div');
        popup.className = 'bg-white p-8 max-w-md w-full mx-auto shadow-2xl';
        popup.style.animation = 'slideIn 0.3s ease-out';
        
        popup.innerHTML = `
          <div class="text-center">
            <div class="mb-6">
              <svg class="w-16 h-16 mx-auto text-green-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
              </svg>
              <h3 class="text-xl font-semibold mb-2">Added to Cart!</h3>
              <p class="text-base opacity-75">
                <span class="font-medium">${this.productTitle}</span> - <span class="font-medium">${this.productPrice}</span> is added to cart
              </p>
            </div>
            <div class="flex flex-col sm:flex-row gap-4 justify-center">
              <button class="goto-cart-btn button inline-flex focus:ring-0 items-center cursor-pointer justify-center transition-colors tracking-(--letter-spacing-button) px-(--button-px) h-(--button-height) text-(size:--button-font-size)/solid bg-(--button-primary-surface) text-(--button-primary-text) font-(--button-font-weight) hover:brightness-95 button-medium primary text-only relative">
                GO TO CART
              </button>
              <button class="continue-btn button inline-flex focus:ring-0 items-center cursor-pointer justify-center transition-colors tracking-(--letter-spacing-button) px-(--button-px) h-(--button-height) text-(size:--button-font-size)/solid bg-transparent text-black border border-black font-(--button-font-weight) hover:bg-black hover:text-white button-medium secondary text-only relative">
                CONTINUE
              </button>
            </div>
          </div>
        `;
        
        backdrop.appendChild(popup);
        document.body.appendChild(backdrop);
        
        // Add event listeners
        const gotoCartBtn = popup.querySelector('.goto-cart-btn');
        const continueBtn = popup.querySelector('.continue-btn');
        
        gotoCartBtn.addEventListener('click', () => {
          window.location.href = '/cart';
        });
        
        continueBtn.addEventListener('click', () => {
          this.closePopup(backdrop);
        });
        
        // Close on backdrop click
        backdrop.addEventListener('click', (e) => {
          if (e.target === backdrop) {
            this.closePopup(backdrop);
          }
        });
        
        // Close on escape key
        const handleEscape = (e) => {
          if (e.key === 'Escape') {
            this.closePopup(backdrop);
            document.removeEventListener('keydown', handleEscape);
          }
        };
        document.addEventListener('keydown', handleEscape);
        
        // Auto close after 10 seconds
        setTimeout(() => {
          if (document.body.contains(backdrop)) {
            this.closePopup(backdrop);
          }
        }, 10000);
      }

      closePopup(backdrop) {
        backdrop.style.animation = 'fadeOut 0.3s ease-out';
        setTimeout(() => {
          if (document.body.contains(backdrop)) {
            document.body.removeChild(backdrop);
          }
        }, 300);
      }

      showErrorMessage(message) {
        // Simple error display - you can customize this
        const errorDiv = document.createElement('div');
        errorDiv.className = 'fixed top-4 right-4 bg-red-500 text-white p-4 rounded-lg z-50 max-w-sm';
        errorDiv.textContent = message;
        
        document.body.appendChild(errorDiv);
        
        setTimeout(() => {
          if (document.body.contains(errorDiv)) {
            document.body.removeChild(errorDiv);
          }
        }, 5000);
      }
    }
  );
}

// Add required CSS animations
const style = document.createElement('style');
style.textContent = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes fadeOut {
    from { opacity: 1; }
    to { opacity: 0; }
  }
  @keyframes slideIn {
    from { 
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
    to { 
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;
document.head.appendChild(style);

console.log('Order Sample component loaded successfully');